// Process Utilities Phase G — conceptual distribution routes and budgetary
// pipe sizing (product decisions 2026-09-13). Pure functions; nothing here
// prices anything (Phase H maps the effective Material + Size + LF to catalog
// assemblies) and nothing here redefines demand (a segment's design flow is
// the sum of what it serves — never derived from route geometry).
//
//   Global standard (UtilitySizingStandards / PipeMaterials / PipeSizes, db/93,
//   Setup ▸ Basis of Design ▸ Process & Utilities)
//     → Project override (UtilitySystems.MaterialKey / SizingMethod /
//       Min- / MaxVelocityFps / MaxHeadLossFtPer100Ft, db/24)
//       → Effective rule, every field with provenance          resolveSizingRule()
//
//   Route:  Source / Plant → Main → Branch → Drop (equipment / coil / demand
//   node), UtilityRouteSegments rows (db/24). Per segment:
//     designFlow      Σ downstream served flow, in the system's governing units
//                     (a drop = its connection's per-unit flow × operating units,
//                     the same rows Phase E's connected demand is built from)
//     calculatedLF    GeometryJson polyline length when drawn, else null
//     effectiveLF     OverrideLF ?? calculatedLF        (null → warning, never invented)
//     calculatedSize  drop with a connection BranchSize → that size (source
//                     'connection'); else the effective rule; else Sizing Required
//     effectiveSize   OverrideSize ?? calculatedSize
//     velocity        actual ft/s at the effective size, when the size is in the table
//
//   SIZING STRATEGIES (SIZING_STRATEGIES): only 'velocity' is implemented —
//   the chilled-water liquid rule: smallest APPROVED size in the material's
//   table whose velocity at the design GPM does not exceed the maximum normal
//   design velocity; never upsized to reach the minimum preferred velocity
//   (a Low Velocity informational warning instead); no size in the table
//   satisfies the limit → Sizing Required. 'head_loss' (MaxHeadLossFtPer100Ft)
//   is a reserved criterion for larger hydronic mains — stored, not evaluated.
//   Every other utility (compressed gases, vacuum, steam / condensate, drains,
//   PW / WFI / hygienic) is 'sizing_required' until its own approved strategy
//   exists; the velocity rule is never reused for them (no SCFM ÷ velocity).
//
//   V (ft/s) = 0.4085 × Q (GPM) ÷ d² (in)   — 1 GPM = 231 in³/min; A = π d²/4.
//
// This is a budgetary conceptual design. It does not claim final pump head,
// balancing, coil pressure drops, total system pressure drop, coordinated
// routing or IFC sizing.

import { UTILITY_BY_KEY, utilityLabel } from "../config/utilityTypes";
import { convertFlow } from "./utilityDemand";
import { refrigerantLinesFor } from "../config/systemArchitectures";

const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const r = (v, d = 2) => v == null ? null : Math.round(v * 10 ** d) / 10 ** d;
const str = (v) => { const t = v === null || v === undefined ? "" : String(v).trim(); return t || null; };

export const GPM_VELOCITY_CONSTANT = 0.4085; // ft/s per (GPM ÷ in²)

export const SIZING_STRATEGIES = [
  { key: "velocity",        label: "Velocity band (liquids)",              implemented: true,  needs: ["maxVelocityFps", "materialKey"] },
  { key: "head_loss",       label: "Max head loss per 100 ft (reserved)",  implemented: false, needs: ["maxHeadLossFtPer100Ft", "materialKey"] },
  { key: "sizing_required", label: "Sizing Required (no approved strategy)", implemented: true, needs: [] },
];
export const STRATEGY_BY_KEY = Object.fromEntries(SIZING_STRATEGIES.map(s => [s.key, s]));

export const SEGMENT_LEVELS = [
  { key: "source", label: "Source / Plant", order: 0 },
  { key: "main",   label: "Main",           order: 1 },
  { key: "branch", label: "Branch",         order: 2 },
  { key: "drop",   label: "Drop / demand node", order: 3 },
];
export const LEVEL_BY_KEY = Object.fromEntries(SEGMENT_LEVELS.map(l => [l.key, l]));

// Provenance of a resolved rule field / a segment size.
export const SIZING_SOURCES = {
  standard:   { key: "standard",   label: "CleanSpace standard",        mark: "" },
  project:    { key: "project",    label: "Project system record",      mark: "‡" },
  override:   { key: "override",   label: "Estimator override",         mark: "*" },
  connection: { key: "connection", label: "Connection branch size",     mark: "" },
  none:       { key: "none",       label: "Sizing Required",            mark: "" },
};

// ---- velocity -------------------------------------------------------------------------------------

export function velocityFps(gpm, insideDiameterIn) {
  const q = num(gpm), d = num(insideDiameterIn);
  if (q === null || d === null || d <= 0) return null;
  return GPM_VELOCITY_CONSTANT * q / (d * d);
}

// ---- configuration --------------------------------------------------------------------------------

// { standards: [UtilitySizingStandards rows], materials: [PipeMaterials rows], sizes: [PipeSizes rows] }
export function indexSizingConfig(config = {}) {
  const standards = new Map((config.standards || []).map(s => [s.UtilityType, s]));
  const materials = new Map((config.materials || []).map(m => [m.MaterialKey, m]));
  const sizesByMaterial = new Map();
  for (const row of config.sizes || []) {
    const list = sizesByMaterial.get(row.MaterialKey) || [];
    list.push(row);
    sizesByMaterial.set(row.MaterialKey, list);
  }
  for (const list of sizesByMaterial.values()) list.sort((a, b) => (num(a.NominalIn) ?? 0) - (num(b.NominalIn) ?? 0) || (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0));
  return { standards, materials, sizesByMaterial };
}

// Normalize a nominal label for lookup: '2"' / '2 in' / '2.0' → '2'; '1 1/2' → '1-1/2'.
export function normalizeNominal(label) {
  const t = str(label);
  if (!t) return null;
  let s = t.toLowerCase().replace(/(in\.?|inch(es)?|")\s*$/, "").trim().replace(/\s+/g, " ");
  s = s.replace(/^(\d+) (\d+\/\d+)$/, "$1-$2");
  if (/^\d+(\.\d+)?$/.test(s)) { const n = Number(s); s = Number.isInteger(n) ? String(n) : String(n); }
  return s;
}
export function findPipeSize(sizes = [], label) {
  const key = normalizeNominal(label);
  if (!key) return null;
  return sizes.find(sz => normalizeNominal(sz.NominalLabel) === key || String(num(sz.NominalIn)) === key) || null;
}

// The effective sizing rule for one system: every field resolved Global
// standard → Project record → Effective with provenance. `method.effective`
// degrades to 'sizing_required' (with `reason`) when the chosen method is not
// implemented or its inputs are incomplete — nothing is ever silently sized.
export function resolveSizingRule(utilityType, { record = null, config = null } = {}) {
  const idx = config?.standards instanceof Map ? config : indexSizingConfig(config || {});
  const standard = idx.standards.get(utilityType) || null;
  const field = (recordKey, standardKey, opts = {}) => {
    const own = opts.text ? str(record?.[recordKey]) : num(record?.[recordKey]);
    if (own !== null && (opts.text || own >= 0)) return { value: own, source: "project", label: SIZING_SOURCES.project.label, mark: SIZING_SOURCES.project.mark };
    const std = opts.text ? str(standard?.[standardKey]) : num(standard?.[standardKey]);
    if (std !== null) return { value: std, source: "standard", label: SIZING_SOURCES.standard.label, mark: "" };
    return { value: null, source: "none", label: "not configured", mark: "" };
  };
  const methodField = field("SizingMethod", "SizingMethod", { text: true });
  const requested = STRATEGY_BY_KEY[methodField.value] ? methodField.value : "sizing_required";
  const minVelocityFps = field("MinVelocityFps", "MinVelocityFps");
  const maxVelocityFps = field("MaxVelocityFps", "MaxVelocityFps");
  const maxHeadLossFtPer100Ft = field("MaxHeadLossFtPer100Ft", "MaxHeadLossFtPer100Ft");
  const materialKey = field("MaterialKey", "DefaultMaterialKey", { text: true });
  const material = materialKey.value ? idx.materials.get(materialKey.value) || null : null;
  const sizes = materialKey.value ? (idx.sizesByMaterial.get(materialKey.value) || []) : [];
  const approvedSizes = sizes.filter(sz => sz.IsApproved !== false && sz.IsApproved !== 0);

  let effective = requested, reason = null;
  const strategy = STRATEGY_BY_KEY[requested];
  if (requested !== "sizing_required") {
    if (!strategy.implemented) { effective = "sizing_required"; reason = `${strategy.label} is a reserved criterion — not evaluated yet.`; }
    else if (maxVelocityFps.value === null) { effective = "sizing_required"; reason = "no maximum design velocity configured."; }
    else if (!materialKey.value) { effective = "sizing_required"; reason = "no pipe material selected."; }
    else if (!material) { effective = "sizing_required"; reason = `pipe material '${materialKey.value}' is not in the configuration.`; }
    else if (!approvedSizes.length) { effective = "sizing_required"; reason = `no approved sizes configured for ${material.Label}.`; }
    else if (material.IsActive === false || material.IsActive === 0) reason = `${material.Label} is retired — still used for this system.`;
  } else if (!standard && !record?.SizingMethod) reason = `no CleanSpace sizing standard exists for ${utilityLabel(utilityType)}.`;
  if (minVelocityFps.value !== null && maxVelocityFps.value !== null && minVelocityFps.value > maxVelocityFps.value) reason = (reason ? reason + " " : "") + "minimum velocity exceeds the maximum — check the configuration.";

  return {
    utilityType, standard, record,
    method: { requested, effective, source: methodField.source, label: STRATEGY_BY_KEY[effective].label, reason },
    minVelocityFps, maxVelocityFps, maxHeadLossFtPer100Ft,
    materialKey, material, materialLabel: material?.Label || materialKey.value || null,
    sizes, approvedSizes,
    implemented: effective !== "sizing_required",
  };
}

// ---- size selection -------------------------------------------------------------------------------

// Velocity-band selection over the rule's approved sizes.
//   → { nominal, insideDiameterIn, velocityFps, method: 'velocity', source, candidates, warnings }
//     or { nominal: null, ..., warnings } when no approved size satisfies the limit.
export function selectPipeSize(designGpm, rule) {
  const q = num(designGpm);
  const warnings = [];
  const out = { nominal: null, pipeSizeId: null, insideDiameterIn: null, velocityFps: null, method: rule.method.effective, source: rule.method.effective === "sizing_required" ? "none" : rule.method.source, candidates: [], warnings };
  if (rule.method.effective !== "velocity") { warnings.push({ code: "sizing_required", message: `Sizing Required — ${rule.method.reason || "no approved sizing strategy"}` }); return out; }
  if (q === null || q <= 0) { warnings.push({ code: "no_flow", message: "No design flow served — nothing to size." }); return out; }
  const vmax = rule.maxVelocityFps.value, vmin = rule.minVelocityFps.value;
  out.candidates = rule.approvedSizes.map(sz => {
    const v = velocityFps(q, sz.InsideDiameterIn);
    return { nominal: sz.NominalLabel, pipeSizeId: sz.PipeSizeID, insideDiameterIn: num(sz.InsideDiameterIn), velocityFps: r(v, 3), withinMax: v !== null && v <= vmax, belowMin: vmin !== null && v !== null && v < vmin };
  });
  const pick = out.candidates.find(c => c.withinMax);
  if (!pick) {
    warnings.push({ code: "no_size_in_table", message: `Sizing Required — even the largest approved ${rule.materialLabel} size (${out.candidates[out.candidates.length - 1]?.nominal ?? "?"} in.) exceeds ${vmax} ft/s at ${r(q, 1)} GPM.` });
    return out;
  }
  out.nominal = pick.nominal; out.pipeSizeId = pick.pipeSizeId; out.insideDiameterIn = pick.insideDiameterIn; out.velocityFps = pick.velocityFps;
  if (pick.belowMin) warnings.push({ code: "low_velocity", level: "info", message: `Low velocity: ${pick.velocityFps} ft/s at ${pick.nominal} in. is below the ${vmin} ft/s minimum preferred velocity (smallest size within ${vmax} ft/s kept — not upsized).` });
  return out;
}

// Velocity check of a size the estimator chose or a connection specified.
export function checkSize(label, designGpm, rule) {
  const warnings = [];
  const sz = findPipeSize(rule.sizes, label);
  const q = num(designGpm);
  if (!sz) {
    if (rule.materialLabel) warnings.push({ code: "size_not_in_table", level: "info", message: `${label} is not in the ${rule.materialLabel} size table — velocity not checked.` });
    return { nominal: str(label), pipeSizeId: null, insideDiameterIn: null, velocityFps: null, inTable: false, warnings };
  }
  const v = q !== null && q > 0 ? velocityFps(q, sz.InsideDiameterIn) : null;
  if (sz.IsApproved === false || sz.IsApproved === 0) warnings.push({ code: "size_not_approved", message: `${sz.NominalLabel} in. is in the ${rule.materialLabel} table but not an approved size.` });
  if (v !== null && rule.maxVelocityFps.value !== null && v > rule.maxVelocityFps.value) warnings.push({ code: "velocity_exceeds_max", message: `${r(v, 2)} ft/s at ${sz.NominalLabel} in. exceeds the ${rule.maxVelocityFps.value} ft/s maximum design velocity.` });
  else if (v !== null && rule.minVelocityFps.value !== null && v < rule.minVelocityFps.value) warnings.push({ code: "low_velocity", level: "info", message: `Low velocity: ${r(v, 2)} ft/s at ${sz.NominalLabel} in. is below the ${rule.minVelocityFps.value} ft/s minimum preferred velocity.` });
  return { nominal: sz.NominalLabel, pipeSizeId: sz.PipeSizeID, insideDiameterIn: num(sz.InsideDiameterIn), velocityFps: r(v, 3), inTable: true, warnings };
}

// ---- geometry -------------------------------------------------------------------------------------

// Length of a {points:[{x,y}...]} polyline in world-feet; null when absent / malformed.
export function polylineLengthFt(geometryJson) {
  if (!geometryJson) return null;
  let g = geometryJson;
  if (typeof g === "string") { try { g = JSON.parse(g); } catch { return null; } }
  const pts = Array.isArray(g?.points) ? g.points : null;
  if (!pts || pts.length < 2) return null;
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = num(pts[i].x) - num(pts[i - 1].x), dy = num(pts[i].y) - num(pts[i - 1].y);
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
    len += Math.hypot(dx, dy);
  }
  return len;
}

// Direction changes along a drawn polyline = conceptual elbows (Phase G canvas
// routing). A vertex counts when the heading changes by at least minAngleDeg
// (a traced run's angle lock is 22.5°, so a real turn is never below that);
// null without geometry, 0 for a straight run. A COUNT for the fittings
// allowance — never a size, never a fitting type.
export function polylineTurns(geometryJson, { minAngleDeg = 20 } = {}) {
  if (!geometryJson) return null;
  let g = geometryJson;
  if (typeof g === "string") { try { g = JSON.parse(g); } catch { return null; } }
  const pts = Array.isArray(g?.points) ? g.points : null;
  if (!pts || pts.length < 2) return null;
  let turns = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const a = Math.atan2(num(pts[i].y) - num(pts[i - 1].y), num(pts[i].x) - num(pts[i - 1].x));
    const b = Math.atan2(num(pts[i + 1].y) - num(pts[i].y), num(pts[i + 1].x) - num(pts[i].x));
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    let d = Math.abs(b - a) * 180 / Math.PI; if (d > 180) d = 360 - d;
    if (d >= minAngleDeg) turns += 1;
  }
  return turns;
}

// Conceptual routing standards (TakeoffVariables category 'utility_routing',
// db/94) with their shipped defaults — valves per segment level and the
// default support spacing. Counts for allowances, never a design.
export const ROUTE_TUNABLE_DEFAULTS = { ROUTE_VALVES_PER_SOURCE: 1, ROUTE_VALVES_PER_MAIN: 0, ROUTE_VALVES_PER_BRANCH: 1, ROUTE_VALVES_PER_DROP: 1, ROUTE_SUPPORT_SPACING_FT: 10 };
const routeTunable = (tunables, key) => { const v = num(tunables?.[key]); return v !== null && v >= 0 ? v : ROUTE_TUNABLE_DEFAULTS[key]; };

// ---- route proposal (regeneration input) -------------------------------------------------------------

const slug = (s) => String(s || "unassigned").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "unassigned";

// Proposed segments for one system from Phase E's rollup (rollupUtilityDemand
// system: `connections` with flowInGoverning / included / roomId / roomLabel /
// tag). One source, one main, one branch per room / location with included
// connections, one drop per included connection. Output = the `segments`
// array sp_SyncUtilityRouteSegments takes; identity = auto_key.
export function proposeRouteSegments(system, { plantTag = null } = {}) {
  const included = (system?.connections || []).filter(c => c.included && c.flowInGoverning !== null);
  const out = [
    { auto_key: "source", segment_level: "source", parent_auto_key: null, from_node: plantTag || `${system.label} source / plant`, to_node: "Main", served_connection_id: null, room_id: null, display_order: 10 },
    { auto_key: "main",   segment_level: "main",   parent_auto_key: "source", from_node: "Main", to_node: "Branches", served_connection_id: null, room_id: null, display_order: 20 },
  ];
  const branches = new Map();
  let order = 100;
  for (const c of included) {
    const key = c.roomId != null ? `branch:room:${c.roomId}` : `branch:loc:${slug(c.roomLabel)}`;
    if (!branches.has(key)) {
      branches.set(key, true);
      out.push({ auto_key: key, segment_level: "branch", parent_auto_key: "main", from_node: "Main", to_node: c.roomLabel || "unassigned", served_connection_id: null, room_id: c.roomId ?? null, display_order: order += 10 });
    }
  }
  for (const c of included) {
    const branchKey = c.roomId != null ? `branch:room:${c.roomId}` : `branch:loc:${slug(c.roomLabel)}`;
    out.push({ auto_key: `drop:conn:${c.connectionId}`, segment_level: "drop", parent_auto_key: branchKey, from_node: c.roomLabel || "Branch", to_node: `${c.tag || c.equipmentDescription || `equipment #${c.equipmentId}`}${c.circuitDesc ? ` (${c.circuitDesc})` : ""}`, served_connection_id: c.connectionId, room_id: c.roomId ?? null, display_order: order += 10 });
  }
  return out;
}

// ---- route evaluation --------------------------------------------------------------------------------

// Evaluate one system's stored segments against its demand rollup and
// effective rule. `system` = a rollupUtilityDemand system (governingUnits,
// connections[] with flowInGoverning / qty / activeUnits / included);
// `includeStandby` follows the record. Returns:
//   { utilityType, systemId, rule, governingUnits, segments: [evaluated], roots,
//     totals: { byMaterialSize: [{ materialKey, materialLabel, nominal, effectiveLF, segmentCount }],
//               sizedLF, unsizedSegments, unmeasuredSegments, designFlowAtSource }, warnings }
export function evaluateUtilityRoute(system, segments = [], { rule, includeStandby = false, zoneFlows = null, tunables = {} } = {}) {
  const governingUnits = system?.governingUnits || null;
  const connById = new Map((system?.connections || []).map(c => [c.connectionId, c]));
  // Scenarios Phase E: an HVAC chilled-water route's demand nodes are AHU zones —
  // zoneFlows = Map(AHUZoneID → { flow (governing units), name }) from the plant chain.
  const zoneFlowById = zoneFlows instanceof Map ? zoneFlows : new Map(Object.entries(zoneFlows || {}).map(([k, v]) => [+k, v]));
  const rows = segments.filter(s => s.UtilitySystemID == null || system?.record?.UtilitySystemID == null || s.UtilitySystemID === system.record.UtilitySystemID);
  const byId = new Map(rows.map(s => [s.SegmentID, s]));
  const children = new Map();
  for (const s of rows) { const p = s.ParentSegmentID != null && byId.has(s.ParentSegmentID) && s.ParentSegmentID !== s.SegmentID ? s.ParentSegmentID : null; children.set(p, [...(children.get(p) || []), s]); }

  const evaluated = new Map();
  const visiting = new Set();
  const served = (s) => {
    const warnings = [];
    if (s.ServedAHUZoneID != null) {
      const z = zoneFlowById.get(s.ServedAHUZoneID);
      if (!z) { warnings.push({ code: "served_zone_missing", message: `Served AHU zone #${s.ServedAHUZoneID} is no longer on this system — no flow attributed; regenerate or re-point the segment.` }); return { flow: 0, warnings, connection: null, zone: null }; }
      if (z.flow === null || z.flow === undefined) { warnings.push({ code: "served_zone_flow_unresolved", message: `${z.name || `zone #${s.ServedAHUZoneID}`}: coil flow not derivable (chilled-water ΔT not set) — 0 attributed.` }); return { flow: 0, warnings, connection: null, zone: z }; }
      return { flow: z.flow, warnings, connection: null, zone: z };
    }
    if (s.ServedConnectionID == null) return { flow: 0, warnings, connection: null };
    const c = connById.get(s.ServedConnectionID);
    if (!c) { warnings.push({ code: "served_connection_missing", message: `Served connection #${s.ServedConnectionID} is no longer on this system — no flow attributed; remove or re-point the segment.` }); return { flow: 0, warnings, connection: null }; }
    if (!c.included || c.flowInGoverning === null) { warnings.push({ code: "served_connection_excluded", message: `${c.tag || c.equipmentDescription}: connection has no usable flow (excluded from the demand rollup) — 0 attributed.` }); return { flow: 0, warnings, connection: c }; }
    return { flow: c.flowInGoverning * (includeStandby ? c.qty : c.activeUnits), warnings, connection: c };
  };
  const evalSeg = (s) => {
    if (evaluated.has(s.SegmentID)) return evaluated.get(s.SegmentID);
    if (visiting.has(s.SegmentID)) return { designFlow: 0, warnings: [{ code: "route_cycle", message: "Segment parent chain forms a cycle." }] };
    visiting.add(s.SegmentID);
    const warnings = [];
    const own = served(s);
    warnings.push(...own.warnings);
    const kids = children.get(s.SegmentID) || [];
    const downstream = kids.reduce((t, k) => t + (evalSeg(k).designFlow || 0), 0);
    const designFlow = own.flow + downstream;
    if (s.ServedConnectionID == null && s.ServedAHUZoneID == null && !kids.length && s.SegmentLevel !== "source") warnings.push({ code: "segment_serves_nothing", message: "Segment serves no connection and has no downstream segments — design flow 0." });
    const calculatedLF = polylineLengthFt(s.GeometryJson);
    const overrideLF = num(s.OverrideLF);
    const effectiveLF = overrideLF ?? calculatedLF;
    if (effectiveLF === null) warnings.push({ code: "length_required", message: "No route length — draw the route or enter an LF; an LF is never invented." });

    // Size: connection BranchSize on a drop → rule → Sizing Required; override wins.
    const gpm = governingUnits ? convertFlow(designFlow, governingUnits, "gpm") : null;
    let calculated = { nominal: null, insideDiameterIn: null, velocityFps: null, source: "none", method: rule?.method.effective || "sizing_required", warnings: [] };
    if (s.SegmentLevel === "drop" && own.connection?.branchSize) {
      const chk = rule ? checkSize(own.connection.branchSize, gpm, rule) : { nominal: own.connection.branchSize, insideDiameterIn: null, velocityFps: null, inTable: false, warnings: [] };
      calculated = { ...chk, source: "connection", method: "connection_branch_size", warnings: chk.warnings };
    } else if (rule && rule.method.effective === "velocity") {
      if (gpm === null && designFlow > 0) calculated.warnings.push({ code: "sizing_required", message: `Sizing Required — ${governingUnits} cannot be converted to GPM for the velocity rule.` });
      else { const sel = selectPipeSize(gpm ?? 0, rule); calculated = { ...sel, source: sel.nominal ? rule.method.source : "none" }; }
    } else if (rule) {
      calculated.warnings.push({ code: "sizing_required", message: `Sizing Required — ${rule.method.reason || `no approved sizing strategy for ${utilityLabel(system?.utilityType)}`}` });
    }
    const overrideSize = str(s.OverrideSize);
    let override = null;
    if (overrideSize) override = rule ? { ...checkSize(overrideSize, gpm, rule), source: "override" } : { nominal: overrideSize, insideDiameterIn: null, velocityFps: null, inTable: false, source: "override", warnings: [] };
    const effective = override || calculated;
    warnings.push(...(override ? override.warnings : calculated.warnings));
    if (override && calculated.nominal && normalizeNominal(calculated.nominal) !== normalizeNominal(override.nominal)) warnings.push({ code: "size_overridden", level: "info", message: `Estimator override ${override.nominal} in. differs from the calculated ${calculated.nominal} in.` });
    if (!effective.nominal) warnings.push({ code: "size_required", message: "Sizing Required — no effective size on this segment." });

    const result = {
      segmentId: s.SegmentID, utilitySystemId: s.UtilitySystemID, parentSegmentId: byId.has(s.ParentSegmentID) ? s.ParentSegmentID : null,
      level: s.SegmentLevel, levelLabel: LEVEL_BY_KEY[s.SegmentLevel]?.label || s.SegmentLevel, autoKey: s.AutoKey || null, isManual: !s.AutoKey,
      fromNode: s.FromNode || null, toNode: s.ToNode || null, servedConnectionId: s.ServedConnectionID ?? null, servedZoneId: s.ServedAHUZoneID ?? null, roomId: s.RoomID ?? null,
      servedTag: own.connection?.tag || own.zone?.name || null,
      designFlow: r(designFlow, 3), designGpm: gpm === null ? null : r(gpm, 3), units: governingUnits,
      calculatedLF: r(calculatedLF, 2), overrideLF, effectiveLF: r(effectiveLF, 2), lfSource: overrideLF !== null ? "override" : (calculatedLF !== null ? "geometry" : "none"),
      turns: polylineTurns(s.GeometryJson), // conceptual elbows from the drawn route (null = not drawn)
      // Valves: a topology count per segment level (ROUTE_VALVES_PER_<LEVEL>); supports: CEILING(effective LF ÷ spacing),
      // spacing from the pipe-size table for the effective size, else the default standard — counts for allowances only.
      valves: Math.round(routeTunable(tunables, `ROUTE_VALVES_PER_${String(s.SegmentLevel || "branch").toUpperCase()}`)),
      // Tees: the route TREE implies one take-off tee on this run per child segment (a source header: one less than
      // its mains — the first main is the header itself). Priced by this run's material + size; reducing tees not distinguished.
      tees: s.SegmentLevel === "source" ? Math.max(0, kids.length - 1) : kids.length,
      ...(() => {
        const sizeRow = effective.nominal && rule?.sizes ? findPipeSize(rule.sizes, effective.nominal) : null;
        const tableSpacing = num(sizeRow?.SupportSpacingFt);
        const spacing = tableSpacing !== null && tableSpacing > 0 ? tableSpacing : routeTunable(tunables, "ROUTE_SUPPORT_SPACING_FT");
        const supports = effectiveLF !== null && spacing > 0 ? Math.ceil(effectiveLF / spacing) : null;
        return { supports, supportSpacingFt: supports !== null ? spacing : null, supportsBasis: supports === null ? "none" : (tableSpacing !== null && tableSpacing > 0 ? "size_table" : "default") };
      })(),
      verticalLiftFt: num(s.VerticalLiftFt), equivalentLF: effectiveLF === null ? null : r(effectiveLF + (num(s.VerticalLiftFt) ?? 0), 2), // Phase F: run + lift where known
      calculatedSize: calculated.nominal, calculatedVelocityFps: calculated.velocityFps, calculatedSizeSource: calculated.source,
      overrideSize: override?.nominal || null,
      effectiveSize: effective.nominal, effectiveInsideDiameterIn: effective.insideDiameterIn ?? null, effectiveVelocityFps: effective.velocityFps ?? null,
      sizeSource: effective.nominal ? effective.source : "none", sizeSourceLabel: SIZING_SOURCES[effective.nominal ? effective.source : "none"]?.label,
      sizingMethod: override ? "override" : calculated.method, sizingBasis: rule ? `${rule.method.label}${rule.materialLabel ? ` · ${rule.materialLabel}` : ""}${rule.maxVelocityFps.value !== null ? ` · ≤ ${rule.maxVelocityFps.value} ft/s (${rule.maxVelocityFps.label})` : ""}` : "no rule",
      materialKey: rule?.materialKey.value || null, materialLabel: rule?.materialLabel || null,
      isLocked: !!s.IsLocked, notes: s.Notes || null, displayOrder: s.DisplayOrder ?? 100,
      candidates: calculated.candidates || [],
      warnings,
    };
    visiting.delete(s.SegmentID);
    evaluated.set(s.SegmentID, result);
    return result;
  };
  for (const s of rows) evalSeg(s);

  const list = rows.map(s => evaluated.get(s.SegmentID));
  const roots = list.filter(x => x.parentSegmentId === null);
  const byMS = new Map();
  let sizedLF = 0;
  for (const x of list) {
    if (x.effectiveSize && x.effectiveLF !== null) {
      const key = `${x.materialKey || "?"}|${x.effectiveSize}`;
      const e = byMS.get(key) || { materialKey: x.materialKey, materialLabel: x.materialLabel, nominal: x.effectiveSize, effectiveLF: 0, segmentCount: 0, sizedByRule: 0, sizedByOverride: 0, sizedByConnection: 0, elbows: 0, valves: 0, supports: 0, tees: 0 };
      e.effectiveLF += x.effectiveLF; e.segmentCount += 1; e.elbows += x.turns || 0; e.valves += x.valves || 0; e.supports += x.supports || 0; e.tees += x.tees || 0; // accessories priced per material + size
      if (x.sizeSource === "override") e.sizedByOverride += 1; else if (x.sizeSource === "connection") e.sizedByConnection += 1; else e.sizedByRule += 1;
      byMS.set(key, e); sizedLF += x.effectiveLF;
    }
  }
  const totals = {
    byMaterialSize: [...byMS.values()].map(e => ({ ...e, effectiveLF: r(e.effectiveLF, 2) })).sort((a, b) => (num(findNominal(a.nominal)) ?? 0) - (num(findNominal(b.nominal)) ?? 0)),
    sizedLF: r(sizedLF, 2),
    unsizedSegments: list.filter(x => !x.effectiveSize).length,
    unmeasuredSegments: list.filter(x => x.effectiveLF === null).length,
    designFlowAtSource: r(roots.reduce((t, x) => t + (x.designFlow || 0), 0), 3),
    // Phase G canvas routing: how much of the route is drawn and the elbows it implies (a count, not priced here).
    drawnSegments: list.filter(x => x.calculatedLF !== null).length,
    elbows: list.reduce((t, x) => t + (x.turns || 0), 0),
    elbowsBasis: list.some(x => x.turns !== null) ? "geometry" : "none",
    elbowsUnsized: list.filter(x => !x.effectiveSize).reduce((t, x) => t + (x.turns || 0), 0), // turns on Sizing Required segments — never priced
    // Valves + supports (2026-09-13): counts; an unsized segment's are never priced, an unmeasured segment has no supports.
    valves: list.reduce((t, x) => t + (x.valves || 0), 0),
    valvesUnsized: list.filter(x => !x.effectiveSize).reduce((t, x) => t + (x.valves || 0), 0),
    supports: list.reduce((t, x) => t + (x.supports || 0), 0),
    supportsUnsized: list.filter(x => !x.effectiveSize || x.effectiveLF === null).reduce((t, x) => t + (x.supports || 0), 0),
    supportsUnmeasured: list.filter(x => x.effectiveLF === null).length,
    supportsBasis: list.some(x => x.supportsBasis === "size_table") ? (list.some(x => x.supportsBasis === "default") ? "mixed" : "size_table") : (list.some(x => x.supportsBasis === "default") ? "default" : "none"),
    // Tees implied by the route tree (take-offs); a tee on a Sizing Required run is never priced.
    tees: list.reduce((t, x) => t + (x.tees || 0), 0),
    teesUnsized: list.filter(x => !x.effectiveSize).reduce((t, x) => t + (x.tees || 0), 0),
  };
  const warnings = list.flatMap(x => x.warnings.map(w => ({ ...w, segmentId: x.segmentId })));
  if (list.length && system?.connectedDemand != null && Math.abs((totals.designFlowAtSource || 0) - system.connectedDemand) > 1e-6) warnings.push({ code: "route_flow_differs_from_connected", level: "info", message: `Route serves ${totals.designFlowAtSource} ${governingUnits} at the source; the system's connected demand is ${system.connectedDemand} ${governingUnits} — regenerate the route or check drops.` });
  return { utilityType: system?.utilityType || null, systemId: system?.record?.UtilitySystemID ?? null, rule, governingUnits, segments: list, roots, totals, warnings };
}
const findNominal = (label) => { const t = normalizeNominal(label); if (!t) return null; const m = t.match(/^(\d+)(?:-(\d+)\/(\d+))?$/); if (m) return Number(m[1]) + (m[2] ? Number(m[2]) / Number(m[3]) : 0); const f = t.match(/^(\d+)\/(\d+)$/); return f ? Number(f[1]) / Number(f[2]) : num(t); };

// Every system of a revision: rollup systems × records × stored segments × config.
//   → { routes: [evaluateUtilityRoute result + label + record], warnings }
export function evaluateUtilityRoutes({ systems = [], utilitySystems = [], segments = [], config = null, tunables = {} } = {}) {
  const idx = indexSizingConfig(config || {});
  const routes = systems.map(sys => {
    const record = sys.record || utilitySystems.find(u => u.UtilityType === sys.utilityType) || null;
    const rule = resolveSizingRule(sys.utilityType, { record, config: idx });
    const own = record ? segments.filter(s => s.UtilitySystemID === record.UtilitySystemID) : [];
    const route = evaluateUtilityRoute({ ...sys, record }, own, { rule, includeStandby: !!record?.IncludeStandbyInConnected, tunables });
    return { ...route, label: sys.label, record, kind: UTILITY_BY_KEY[sys.utilityType]?.kind || null };
  });
  return { routes, warnings: routes.flatMap(rt => rt.warnings.map(w => ({ ...w, utilityType: rt.utilityType }))) };
}

// Phase H input: the effective Material + Nominal size + LF per system, plus
// what still lacks a size or a length. No prices here.
export function distributionTakeoff(routes = []) {
  return routes.map(rt => ({
    utilityType: rt.utilityType, label: rt.label, materialKey: rt.rule?.materialKey.value || null, materialLabel: rt.rule?.materialLabel || null,
    rows: rt.totals.byMaterialSize, sizedLF: rt.totals.sizedLF,
    unsizedSegments: rt.totals.unsizedSegments, unmeasuredSegments: rt.totals.unmeasuredSegments, segmentCount: rt.segments.length,
  }));
}

// ---- Scenarios Phase E: HVAC chilled-water route on the same model ------------------------------------

// Proposed segments for one scenario chilled-water system (evaluateScenarios'
// system with .plant.chilledWater): chiller plant (source) → CHWS/CHWR main →
// one branch per served AHU zone (the coil). Identity = auto_key
// 'branch:zone:<AHUZoneID>'; demand nodes are served_ahu_zone_id.
export function proposeHvacRouteSegments(system, { plantTag = null } = {}) {
  const branches = system?.plant?.chilledWater?.branches || [];
  const out = [
    { auto_key: "source", segment_level: "source", parent_auto_key: null, from_node: plantTag || `${system?.SystemKey || "CHW"} chiller plant`, to_node: "CHWS / CHWR main", served_connection_id: null, served_ahu_zone_id: null, room_id: null, display_order: 10 },
    { auto_key: "main",   segment_level: "main",   parent_auto_key: "source", from_node: "CHWS / CHWR main", to_node: "AHU branches", served_connection_id: null, served_ahu_zone_id: null, room_id: null, display_order: 20 },
  ];
  let order = 100;
  for (const b of branches) out.push({ auto_key: `branch:zone:${b.zoneId}`, segment_level: "branch", parent_auto_key: "main", from_node: "Main", to_node: `${b.zoneName} coil`, served_connection_id: null, served_ahu_zone_id: b.zoneId, room_id: null, display_order: order += 10 });
  return out;
}

// Phase F — refrigerant line sizing is manufacturer / tonnage specific
// (equivalent length, lift, refrigerant, capacity): it is NEVER fabricated
// here. Every DX run reads Sizing Required until the selected equipment's line
// sizes are entered as per-segment overrides (free text, e.g. '7/8 suction /
// 3/8 liquid'). Same rule shape as resolveSizingRule so the evaluator and the
// UI treat it like any other unapproved strategy.
export const REFRIGERANT_SIZING_RULE = {
  utilityType: "refrigerant", standard: null, record: null,
  method: { requested: "sizing_required", effective: "sizing_required", source: "none", label: "Sizing Required (refrigerant — manufacturer line sizing)", reason: "refrigerant line sizes depend on the selected condensing unit / coil, refrigerant, equivalent length and lift — enter the manufacturer's line sizes as overrides; nothing is assumed." },
  minVelocityFps: { value: null, source: "none" }, maxVelocityFps: { value: null, source: "none" }, maxHeadLossFtPer100Ft: { value: null, source: "none" },
  materialKey: { value: null, source: "none" }, material: null, materialLabel: null, sizes: [], approvedSizes: [], implemented: false,
};

// Proposed segments for one scenario DX system (evaluateScenarios' system with
// .plant.dx): one refrigerant RUN per served AHU zone — condensing unit(s) →
// that zone's DX coil — keyed 'refrig:zone:<AHUZoneID>'. One CU per AHU unit
// (product decision): a zone with Qty N AHUs has N line SETS along that run.
export function proposeDxRouteSegments(system) {
  const units = system?.plant?.dx?.condensingUnits || [];
  let order = 100;
  return units.map(u => ({ auto_key: `refrig:zone:${u.zoneId}`, segment_level: "branch", parent_auto_key: null, from_node: `${u.condensingUnits} × CU (${u.zoneName})`, to_node: `${u.zoneName} DX coil`, served_connection_id: null, served_ahu_zone_id: u.zoneId, room_id: null, display_order: order += 10 }));
}

// Evaluate every chilled-water AND DX system of the evaluated scenarios
// against its HVAC-owned segments.
//   chilled water: sizing rule = db/93 standard → the revision's chilled_water
//   UtilitySystems record; branch flow = the zone's coil GPM.
//   DX: REFRIGERANT_SIZING_RULE (Sizing Required); a run's "flow" = the tons per
//   condensing unit it serves; result.refrigerant = line-set takeoff (lines per
//   set from the system's DistributionType, sets = the zone's CU count, line LF
//   = equivalent length × lines × sets) plus explicit allowances / gaps.
//   → [{ systemId, scenarioId, systemKey, kind: 'chw' | 'dx', ...evaluateUtilityRoute result, refrigerant? }]
export function evaluateHvacRoutes({ scenarios = [], segments = [], config = null, utilitySystems = [], tunables = {} } = {}) {
  const idx = indexSizingConfig(config || {});
  const chwRecord = utilitySystems.find(u => u.UtilityType === "chilled_water") || null;
  const chwRule = resolveSizingRule("chilled_water", { record: chwRecord, config: idx });
  const out = [];
  for (const sc of scenarios) for (const h of sc.systems || []) {
    const own = segments.filter(s => s.HvacSystemID === h.SystemID);
    const chw = h.plant?.chilledWater, dx = h.plant?.dx;
    if (chw) {
      const zoneFlows = new Map(chw.branches.map(b => [b.zoneId, { flow: b.gpm, name: b.zoneName, tons: b.tons }]));
      const pseudo = { utilityType: "chilled_water", label: `${sc.ScenarioKey} ${h.SystemKey}`, governingUnits: "gpm", connections: [], record: null, connectedDemand: chw.plantGpmUndiversified };
      const route = evaluateUtilityRoute(pseudo, own, { rule: chwRule, zoneFlows, tunables });
      out.push({ ...route, kind: "chw", systemId: h.SystemID, scenarioId: sc.ScenarioID, scenarioKey: sc.ScenarioKey, systemKey: h.SystemKey, plantGpm: chw.plantGpm, label: pseudo.label });
    } else if (dx) {
      const units = dx.condensingUnits || [];
      const zoneFlows = new Map(units.map(u => [u.zoneId, { flow: u.tonsPerCu, name: u.zoneName, tons: u.tonsPerCu, cuCount: u.condensingUnits }]));
      const pseudo = { utilityType: "refrigerant", label: `${sc.ScenarioKey} ${h.SystemKey}`, governingUnits: "tons", connections: [], record: null, connectedDemand: r(units.reduce((t, u) => t + (u.tonsPerCu || 0), 0), 3) };
      const route = evaluateUtilityRoute(pseudo, own, { rule: REFRIGERANT_SIZING_RULE, zoneFlows, tunables });
      const lines = refrigerantLinesFor(h.DistributionType);
      const lineSets = route.segments.map(seg => {
        const z = seg.servedZoneId != null ? zoneFlows.get(seg.servedZoneId) : null;
        const sets = z?.cuCount ?? 1;
        const eqLF = seg.equivalentLF;
        return { segmentId: seg.segmentId, zoneName: z?.name || seg.toNode, sets, linesPerSet: lines.length, lines: lines.map(l => l.label), runLF: seg.effectiveLF, verticalLiftFt: seg.verticalLiftFt, equivalentLF: eqLF, lineLF: eqLF === null ? null : r(eqLF * lines.length * sets, 1), tonsPerCu: z?.tons ?? null, sized: !!seg.effectiveSize,
          // every line of every set turns where the run turns and carries the run's isolation-valve count
          elbows: seg.turns == null ? null : seg.turns * lines.length * sets, valves: (seg.valves || 0) * lines.length * sets };
      });
      const warnings = [...route.warnings];
      if (!lines.length && route.segments.length) warnings.push({ code: "no_field_refrigerant_piping", level: "info", message: `${h.SystemKey}: packaged rooftop — factory-charged, no field refrigerant piping; the route is informational.` });
      if (route.segments.length) warnings.push({ code: "refrigerant_charge_unsupported", level: "info", message: `${h.SystemKey}: refrigerant charge allowance needs the selected equipment's charge data — not estimated.` });
      const refrigerant = {
        lines, lineSets,
        totals: {
          lineSetCount: lineSets.reduce((t, x) => t + x.sets, 0), runLF: r(lineSets.reduce((t, x) => t + (x.runLF || 0), 0), 1),
          equivalentLF: r(lineSets.reduce((t, x) => t + (x.equivalentLF || 0), 0), 1), lineLF: r(lineSets.reduce((t, x) => t + (x.lineLF || 0), 0), 1),
          unmeasuredRuns: lineSets.filter(x => x.equivalentLF === null).length, unsizedRuns: lineSets.filter(x => !x.sized).length,
          penetrationAllowance: lineSets.reduce((t, x) => t + x.sets, 0), // one roof / wall penetration per line set — an explicit conceptual allowance, not a count
          elbows: lineSets.reduce((t, x) => t + (x.elbows || 0), 0), // line elbows from the drawn runs (× lines × sets); null-turn runs contribute 0
          elbowsBasis: lineSets.some(x => x.elbows !== null) ? "geometry" : "none",
          valves: lineSets.reduce((t, x) => t + (x.valves || 0), 0), // line service / isolation valves by segment level (× lines × sets)
          chargeAllowanceLb: null,
        },
        assumptions: ["One condensing unit per AHU unit; one refrigerant line set (liquid + suction unless the distribution type says otherwise) per condensing unit along the run.", "Line LF = (run LF + vertical lift) × lines per set × sets. Equivalent length here is run + lift only — fittings equivalents are not applied.", "Line sizes are manufacturer-specific and are entered as overrides, never derived.", "Penetration allowance = one per line set; refrigerant charge is not estimated without the selected equipment's data."],
      };
      out.push({ ...route, warnings, kind: "dx", systemId: h.SystemID, scenarioId: sc.ScenarioID, scenarioKey: sc.ScenarioKey, systemKey: h.SystemKey, refrigerant, label: pseudo.label });
    }
  }
  return out;
}

// Save payload for sp_SaveUtilityRouteSegment (keys present = written). Owner =
// utilitySystemId (process utility) or hvacSystemId (scenario HVAC system).
export function routeSegmentPayload(patch, { revisionId, utilitySystemId = null, hvacSystemId = null }) {
  if (revisionId == null) throw new Error("routeSegmentPayload: revisionId is required");
  const out = { revision_id: revisionId, utility_system_id: utilitySystemId ?? null };
  if (hvacSystemId != null) out.hvac_system_id = hvacSystemId; // HVAC-owned route (Scenarios Phase E)
  if (patch.SegmentID != null) out.segment_id = patch.SegmentID;
  const map = { SegmentLevel: "segment_level", ParentSegmentID: "parent_segment_id", FromNode: "from_node", ToNode: "to_node", ServedConnectionID: "served_connection_id", ServedAHUZoneID: "served_ahu_zone_id", RoomID: "room_id", GeometryJson: "geometry_json", OverrideLF: "override_lf", VerticalLiftFt: "vertical_lift_ft", OverrideSize: "override_size", IsLocked: "is_locked", Notes: "notes", DisplayOrder: "display_order", is_delete: "is_delete" };
  for (const [k, key] of Object.entries(map)) if (k in patch) out[key] = k === "IsLocked" || k === "is_delete" ? (patch[k] ? 1 : 0) : (patch[k] === "" ? null : patch[k]);
  return out;
}
